if (!customElements.get('tata-hero-slideshow')) {
  class TataHeroSlideshow extends HTMLElement {
    connectedCallback() {
      if (this._isReady) return;
      this._isReady = true;

      this.slides = Array.from(this.querySelectorAll('.tata-hero-slideshow__slide'));
      this.bars = Array.from(this.querySelectorAll('.tata-hero-slideshow__bar'));
      this.slideshowEnabled = this.dataset.slideshow === 'true';
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slideshowEnabled;
      this.durationMs = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      this.currentIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (this.currentIndex < 0) this.currentIndex = 0;

      if (!this.slideshowEnabled || this.slides.length < 2) {
        this.slides.forEach((slide, index) => {
          const isActive = index === this.currentIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
        return;
      }

      this._paused = false;
      this._advanceTimer = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.style.setProperty('--tata-hero-duration', `${this.durationMs}ms`);

      this.bars.forEach((bar) => {
        bar.addEventListener('click', () => {
          const index = parseInt(bar.dataset.index, 10);
          if (!Number.isNaN(index)) this.goTo(index, true);
        });
      });

      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.resume());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.resume();
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause();
        else this.resume();
      });

      this.bars.forEach((bar) => {
        const fill = bar.querySelector('.tata-hero-slideshow__bar-fill');
        fill?.addEventListener('animationend', (event) => {
          if (event.target !== fill) return;
          if (!bar.classList.contains('is-active') || this._paused) return;
          this.goTo(this.currentIndex + 1);
        });
      });

      this.syncBars();
      if (this.autoplayEnabled && !this._paused) this.startProgress();
    }

    pause() {
      this._paused = true;
      this.clearAdvanceTimer();
      this.bars.forEach((bar) => {
        const fill = bar.querySelector('.tata-hero-slideshow__bar-fill');
        if (fill) fill.style.animationPlayState = 'paused';
      });
    }

    resume() {
      if (!this.autoplayEnabled || this.slides.length < 2) return;
      this._paused = false;
      this.bars.forEach((bar) => {
        const fill = bar.querySelector('.tata-hero-slideshow__bar-fill');
        if (fill) fill.style.animationPlayState = 'running';
      });
      const activeBar = this.bars[this.currentIndex];
      const fill = activeBar?.querySelector('.tata-hero-slideshow__bar-fill');
      if (this.reducedMotion || !fill || getComputedStyle(fill).animationName === 'none') {
        this.scheduleAdvance();
      }
    }

    clearAdvanceTimer() {
      if (this._advanceTimer) {
        window.clearTimeout(this._advanceTimer);
        this._advanceTimer = null;
      }
    }

    scheduleAdvance() {
      this.clearAdvanceTimer();
      if (!this.autoplayEnabled || this._paused) return;
      this._advanceTimer = window.setTimeout(() => {
        this.goTo(this.currentIndex + 1);
      }, this.durationMs);
    }

    resetBarFill(fill) {
      if (!fill) return;
      fill.style.animation = 'none';
      void fill.offsetWidth;
      fill.style.animation = '';
      fill.style.animationPlayState = this._paused ? 'paused' : 'running';
    }

    syncBars() {
      this.bars.forEach((bar, index) => {
        bar.classList.toggle('is-complete', index < this.currentIndex);
        bar.classList.toggle('is-active', index === this.currentIndex);
        bar.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false');
      });
    }

    startProgress() {
      const activeBar = this.bars[this.currentIndex];
      const fill = activeBar?.querySelector('.tata-hero-slideshow__bar-fill');
      if (!fill) return;

      this.resetBarFill(fill);

      if (this.reducedMotion) {
        fill.style.width = '100%';
        this.scheduleAdvance();
        return;
      }

      activeBar.classList.add('is-active');
      fill.style.animation = `tata-hero-bar-progress ${this.durationMs}ms linear forwards`;
      fill.style.animationPlayState = this._paused ? 'paused' : 'running';
    }

    goTo(index, fromUser = false) {
      if (!this.slideshowEnabled || this.slides.length < 2) return;

      const count = this.slides.length;
      const nextIndex = ((index % count) + count) % count;

      if (!fromUser && nextIndex === this.currentIndex) return;

      this.clearAdvanceTimer();

      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === nextIndex);
        slide.setAttribute('aria-hidden', i === nextIndex ? 'false' : 'true');
      });

      this.currentIndex = nextIndex;
      this.syncBars();

      this.bars.forEach((bar, i) => {
        const fill = bar.querySelector('.tata-hero-slideshow__bar-fill');
        if (!fill) return;
        fill.style.width = i < nextIndex ? '100%' : '0%';
        fill.style.animation = 'none';
      });

      if (this.autoplayEnabled) this.startProgress();
    }
  }

  customElements.define('tata-hero-slideshow', TataHeroSlideshow);
}
